import { roleLabel } from '@shared/lib/roles/roles'
// ========================================
// ENUMS (como const objects para compatibilidade)
// ========================================

export const UserRole = {
  USER: 'USER',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
} as const

export type UserRole = (typeof UserRole)[keyof typeof UserRole]

export const Permission = {
  READ: 'READ',
  WRITE: 'WRITE',
  DELETE: 'DELETE',
  PUBLISH: 'PUBLISH',
  MANAGE_USERS: 'MANAGE_USERS',
  VIEW_ANALYTICS: 'VIEW_ANALYTICS',
  SEND_NOTIFICATIONS: 'SEND_NOTIFICATIONS',
} as const

export type Permission = (typeof Permission)[keyof typeof Permission]

export const CommunicationType = {
  GENERAL: 'GENERAL',
  ANNOUNCEMENT: 'ANNOUNCEMENT',
  ALERT: 'ALERT',
  URGENT: 'URGENT',
  EVENT: 'EVENT',
  NEWS: 'NEWS',
} as const

export type CommunicationType = (typeof CommunicationType)[keyof typeof CommunicationType]

export const CommunicationPriority = {
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const

export type CommunicationPriority =
  (typeof CommunicationPriority)[keyof typeof CommunicationPriority]

export const CommunicationStatus = {
  DRAFT: 'DRAFT',
  IN_REVIEW: 'IN_REVIEW',
  APPROVED: 'APPROVED',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
} as const

export type CommunicationStatus =
  (typeof CommunicationStatus)[keyof typeof CommunicationStatus]

export const CommunicationPurpose = {
  INFORMATIONAL: 'INFORMATIONAL',
  MANDATORY: 'MANDATORY',
} as const

export type CommunicationPurpose =
  (typeof CommunicationPurpose)[keyof typeof CommunicationPurpose]

export const getUserRoleLabel = roleLabel

export function getAuditEntityTypeLabel(entityType?: string | null) {
  switch (entityType?.toUpperCase()) {
    case 'COMMUNICATION':
      return 'Comunicado'
    case 'DEPARTMENT':
      return 'Departamento'
    case 'STORE':
      return 'Loja'
    case 'USER':
      return 'Usuário'
    case 'BUSINESSHOURS':
      return 'Horário Comercial'
    case 'TENANT':
      return 'Organização'
    case 'NOTIFICATION':
      return 'Notificação'
    case 'ATTACHMENT':
      return 'Anexo'
    default:
      return entityType ?? ''
  }
}

export function getCommunicationTypeLabel(type?: string | null) {
  switch (type?.toUpperCase()) {
    case CommunicationType.GENERAL:
    case 'INFORMATIVO':
      return 'Informativo'
    case CommunicationType.ANNOUNCEMENT:
    case 'INSTITUCIONAL':
      return 'Institucional'
    case CommunicationType.ALERT:
      return 'Alerta'
    case CommunicationType.URGENT:
    case 'URGENTE':
      return 'Urgente'
    case CommunicationType.EVENT:
    case 'EVENTO':
      return 'Evento'
    case CommunicationType.NEWS:
      return 'Novidade'
    default:
      return type ?? ''
  }
}

export function getCommunicationPriorityLabel(priority?: string | null) {
  switch (priority?.toUpperCase()) {
    case CommunicationPriority.LOW:
      return 'Baixa'
    case CommunicationPriority.NORMAL:
      return 'Normal'
    case CommunicationPriority.HIGH:
      return 'Alta'
    case CommunicationPriority.CRITICAL:
    case 'URGENT':
      return 'Crítica'
    default:
      return priority ?? ''
  }
}

// ========================================
// USER & AUTH
// ========================================

export interface User {
  id: string
  name: string
  email: string
  avatar?: string
  role: UserRole
  departmentId?: string
  departmentName?: string
  permissions: Permission[]
}

export interface Department {
  id: string
  name: string
  description?: string
  active: boolean
  parentId?: string
  managerId?: string
  createdAt: string
  updatedAt: string
}

// ========================================
// PAGINATION
// ========================================

export interface PaginationParams {
  page?: number
  pageSize?: number
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: PaginationMeta
}

// ========================================
// API RESPONSE
// ========================================

export interface ApiResponse<T> {
  data: T
  metadata?: Record<string, unknown>
}

export interface ApiError {
  message: string
  code: string
  details?: Record<string, string[]>
}

// ========================================
// FILE/ATTACHMENT
// ========================================

export interface Attachment {
  id: string
  name: string
  url: string
  size: number
  type: string
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}
